import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, Building2, DollarSign, Clock } from 'lucide-react';
import { createResource } from '../lib/resources';
import { getResourceRoles, ResourceRole } from '../lib/resourceRoles';

interface ResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
}

export default function ResourceModal({ isOpen, onClose, onSuccess, businessId }: ResourceModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissionRoles, setPermissionRoles] = useState<ResourceRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: '', // Job role like "PPF Technician", "Manager"
    role_id: '', // Permission role ID
    compensation_type: 'hourly' as 'hourly' | 'flat_rate' | 'commission',
    hourly_rate: '0',
    flat_rate_amount: '0',
    commission_percentage: '0',
    status: 'active',
    is_admin: false,
    password: '',
    confirm_password: '',
    notes: ''
  });

  const loadPermissionRoles = async () => {
    if (!businessId) return;
    
    try {
      const roles = await getResourceRoles(businessId);
      setPermissionRoles(roles);
    } catch (err) {
      console.error('Error loading permission roles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permission roles');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadPermissionRoles();
    }
  }, [isOpen, businessId]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Reset form data when modal closes
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        role_id: '',
        compensation_type: 'hourly',
        hourly_rate: '0',
        flat_rate_amount: '0',
        commission_percentage: '0',
        status: 'active',
        is_admin: false,
        password: '',
        confirm_password: '',
        notes: ''
      });
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Basic validation
      if (!formData.first_name.trim()) {
        throw new Error('First name is required');
      }
      if (!formData.last_name.trim()) {
        throw new Error('Last name is required');
      }
      if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        throw new Error('Valid email is required');
      }
      if (!formData.role.trim()) {
        throw new Error('Job role is required');
      }
      if (!formData.password) {
        throw new Error('Password is required');
      }

      // Validate password
      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }
      if (formData.password !== formData.confirm_password) {
        throw new Error('Passwords do not match');
      }

      // Initialize compensation values
      const compensationData = {
        hourly_rate: 0,
        flat_rate_amount: 0,
        commission_percentage: 0,
        commission_rate: 0
      };

      // Set compensation value based on type
      switch (formData.compensation_type) {
        case 'hourly':
          const hourlyRate = parseFloat(formData.hourly_rate || '0');
          if (isNaN(hourlyRate)) {
            throw new Error('Please enter a valid hourly rate');
          }
          compensationData.hourly_rate = hourlyRate;
          break;
        case 'flat_rate':
          const flatRate = parseFloat(formData.flat_rate_amount || '0');
          if (isNaN(flatRate)) {
            throw new Error('Please enter a valid flat rate amount');
          }
          compensationData.flat_rate_amount = flatRate;
          break;
        case 'commission':
          const commissionRate = parseFloat(formData.commission_percentage || '0');
          if (isNaN(commissionRate) || commissionRate > 100) {
            throw new Error('Please enter a valid commission rate (0-100)');
          }
          compensationData.commission_percentage = commissionRate;
          compensationData.commission_rate = commissionRate;
          break;
      }

      // Create the resource payload
      const resourcePayload = {
        business_id: businessId,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        role: formData.role.trim(),
        role_id: formData.role_id || undefined,
        status: formData.status,
        compensation_type: formData.compensation_type,
        is_admin: formData.is_admin,
        password: formData.password,
        confirm_password: formData.confirm_password,
        last_active: new Date().toISOString(),
        earnings_ytd: 0,
        earnings_mtd: 0,
        earnings_last_month: 0,
        is_mobile_available: false,
        ...compensationData
      };

      await createResource(resourcePayload);
      onSuccess();
    } catch (err) {
      console.error('Error creating resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to create team member');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">New Team Member</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-6">Basic Information</h3>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Job Role
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="e.g., PPF Technician, Manager, Installer"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter the team member's job title or position
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Permission Role
                    </label>
                    <select
                      value={formData.role_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    >
                      <option value="">No specific permissions (optional)</option>
                      {permissionRoles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Assign system access permissions to this team member
                    </p>
                  </div>
                </div>
              </div>

              {/* Compensation */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-6">Compensation</h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Compensation Type
                    </label>
                    <select
                      value={formData.compensation_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, compensation_type: e.target.value as 'hourly' | 'flat_rate' | 'commission' }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    >
                      <option value="hourly">Hourly Rate</option>
                      <option value="flat_rate">Flat Rate</option>
                      <option value="commission">Commission</option>
                    </select>
                  </div>
                  
                  {formData.compensation_type === 'hourly' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Hourly Rate
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.hourly_rate}
                          onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                          className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  )}
                  
                  {formData.compensation_type === 'flat_rate' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Flat Rate Amount
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.flat_rate_amount}
                          onChange={(e) => setFormData(prev => ({ ...prev, flat_rate_amount: e.target.value }))}
                          className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  )}
                  
                  {formData.compensation_type === 'commission' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Commission Rate
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={formData.commission_percentage}
                          onChange={(e) => setFormData(prev => ({ ...prev, commission_percentage: e.target.value }))}
                          className="w-full pr-8 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                          placeholder="0"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Login Credentials */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-6">Login Credentials</h3>
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.is_admin}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_admin: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-700">Admin Access</span>
                    </label>
                    <p className="mt-1 text-xs text-gray-500 ml-6">
                      Grant administrative privileges to this team member
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter password"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Required for team members who need system access
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.confirm_password}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Confirm password"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  placeholder="Add any additional notes about this team member..."
                />
              </div>

              <div className="mt-8">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Team Member'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}